import React from 'react';
import { Button } from '@zurich-primitives/button';
import styles from './ActionButtons.module.scss';
import { ReactComponent as EraserIcon } from 'app/assets/Icons/Eraser.svg';
import { ReactComponent as RepeatIcon } from 'app/assets/Icons/Repeat.svg';
import { Switch } from '@zurich-base-ui/components.switch';
import { IActionButtons } from './ActionButtons.interface';
import {
  displayToastError,
  handleFileSelection,
  handleFileValidation,
  HighlightsFeatureTooltipContent
} from 'app/utils';
import {
  getCurrentFile,
  getService,
  setCurrentFile,
  setRegenerateExtraction,
  useAppDispatch,
  useAppSelector
} from 'app/redux';
import { ReactComponent as AttachIcon } from 'app/assets/Icons/Attach.svg';
import { Tooltip } from 'react-tooltip';

export const ActionButtons: React.FC<IActionButtons> = ({
  handleDelete,
  isSwitchOn = false,
  handleInvoiceSwitch = undefined,
  handleRegenerate = undefined,
  showHighlights = false,
  handleShowHighlights = undefined,
  showUploadNewFile = true,
  disableUploadNewFile = false,
  showDeleteFile = true,
  disableDeleteFile = false,
  isPlural = false,
  handleCustomUploadNewFile = undefined
}) => {
  const dispatch = useAppDispatch();
  const currentService = useAppSelector(getService);
  const currentFile = useAppSelector(getCurrentFile);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { files } = e.target;
    const selectedFile: File | undefined = files ? files[0] : undefined;

    if (selectedFile !== undefined) {
      const validation = handleFileValidation(selectedFile, currentService);
      if (validation.valid) {
        dispatch(setCurrentFile(undefined));
        dispatch(setCurrentFile(files?.[0]));
        dispatch(setRegenerateExtraction(true));
      } else {
        const errorMessage = validation.error;
        console.error(errorMessage);
        displayToastError(errorMessage, 'Error uploading file');
      }
    }
  };

  const handleUploadNewFile = (e: any): void => {
    e.stopPropagation();

    if (handleCustomUploadNewFile) {
      handleCustomUploadNewFile();
    } else {
      handleFileSelection(handleFileChange, currentService);
    }
  };

  return (
    <div className={styles.buttons}>
      {showDeleteFile && (
        <Button
          aria-label="Delete files"
          variant="tertiary"
          size="small"
          onClick={handleDelete}
          disabled={disableDeleteFile && currentFile === undefined}
        >
          <Button.Icon>
            <EraserIcon />
          </Button.Icon>
          <Button.Label>
            {isPlural ? 'Delete files' : 'Delete file'}
          </Button.Label>
        </Button>
      )}

      {showUploadNewFile && (
        <div className={styles['attach-btn']}>
          <Button
            aria-label="Upload document"
            variant="tertiary"
            size="small"
            onClick={(e) => handleUploadNewFile(e)}
            disabled={disableUploadNewFile && currentFile === undefined}
          >
            <Button.Icon>
              <AttachIcon />
            </Button.Icon>
            <Button.Label>Upload new file</Button.Label>
          </Button>
        </div>
      )}

      {handleRegenerate && currentFile !== undefined && (
        <Button
          aria-label="Regenerate response"
          variant="tertiary"
          size="small"
          onClick={handleRegenerate}
        >
          <Button.Icon>
            <RepeatIcon />
          </Button.Icon>
          <Button.Label>Regenerate</Button.Label>
        </Button>
      )}

      {handleInvoiceSwitch && (
        <div className={styles['switch-section']}>
          <Switch
            status={isSwitchOn ? 'on' : 'off'}
            onClick={handleInvoiceSwitch}
            id={'invoicecoverage'}
            className={styles['switch-style']}
          />
          <span className={styles['switch-label']}>Invoice?</span>
        </div>
      )}

      <div className={styles['switch-section']}>
        {handleShowHighlights && (
          <>
            <Switch
              status={showHighlights ? 'on' : 'off'}
              onClick={handleShowHighlights}
              id={'highlight'}
              className={styles['switch-style']}
            />
            <span
              className={styles['switch-label']}
              data-tooltip-id="highlight-switch-tooltip"
              data-tooltip-content={HighlightsFeatureTooltipContent}
            >
              Highlight
            </span>
            <span
              style={{
                textAlign: 'center',
                verticalAlign: 'top',
                margin: 0,
                marginLeft: '0.20rem',
                paddingBottom: '0.5rem',
                color: 'gray'
              }}
            >
              *
            </span>
          </>
        )}
      </div>
      <Tooltip
        id="highlight-switch-tooltip"
        className={styles['highlight-switch-tooltip']}
      />
    </div>
  );
};
